import { useState } from "react";
import { Save, FileDown, FileSpreadsheet, FileText, Lock, Unlock, Calendar, Wallet } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StickyActionBar from "@/components/mobile/StickyActionBar";
import { isNewFundCascade } from "@/lib/fundCascade";
import { noNegProps, AdjNote } from "@/lib/fundInput";

// Mobile-only Fund Management layout. Visible only at <md (768px).
// Compact card list for the current month's classwise editor + a scrollable
// monthly history. Heavy table view stays desktop-only.
const CLASS_KEYS = ["balVatika", "primary", "upperPrimary", "highSchool"];
const CLASS_LABELS = {
  balVatika: "Bal Vatika",
  primary: "Primary",
  upperPrimary: "Upper Primary",
  highSchool: "High School",
};

function NumField({ label, value, onChange, readOnly, testId, step = "0.01" }) {
  return (
    <label className="block">
      <span className="block text-[0.6rem] font-semibold uppercase tracking-[0.16em] text-ink-700">{label}</span>
      <input
        data-testid={testId}
        type="number"
        inputMode="decimal"
        step={step}
        value={value ?? 0}
        readOnly={readOnly}
        min="0"
        {...(readOnly ? {} : noNegProps)}
        onChange={(e) => onChange(e.target.value)}
        style={{ fontSize: `${Math.max(0.55, Math.min(0.8, 6.4 / Math.max(String(value ?? 0).length, 1)))}rem` }}
        className={`fit-num mt-1 block w-full min-w-0 rounded-lg border border-border px-1.5 py-2 text-right font-mono tracking-tight text-ink-900 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none outline-none focus:border-clay focus:ring-1 focus:ring-clay/30 ${
          readOnly ? "bg-sand-50 text-ink-700" : "bg-white"
        }`}
      />
    </label>
  );
}

const money = (n, dec = 2) => `Rs ${(Number(n) || 0).toFixed(dec)}`;
const kg = (n) => `${(Number(n) || 0).toFixed(3)} kg`;

// Total consumed; tap to see the MDM + DR split.
function ConsField({ total, mdm, dr, dec, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <button type="button" onClick={() => setOpen((o) => !o)} className="min-w-0 text-left" data-testid={testId}>
      <span className="block text-[0.6rem] font-semibold uppercase tracking-[0.16em] text-ink-700">Cons ▾</span>
      <span className="mt-1 block w-full truncate rounded-lg border border-dashed border-border bg-sand-50 px-1.5 py-2 text-right font-mono text-[0.8rem] text-ink-900" data-testid={`${testId}-total`}>
        {Number(total || 0).toFixed(dec)}
      </span>
      {open && (
        <span className="mt-1 block text-right text-[0.65rem] leading-tight text-ink-700" data-testid={`${testId}-split`}>
          MDM {Number(mdm || 0).toFixed(dec)} + DR {Number(dr || 0).toFixed(dec)}
        </span>
      )}
    </button>
  );
}

export default function MobileFundManagement(props) {
  const {
    active, year, month, date, setYear, setMonth, setDate,
    yearChoices, MONTH_OPTIONS,
    record, consumed, readonly, editingOpening, setEditingOpening,
    isAnchorMonth = true, prevMonthLabel = "", onOpenFundAdj = null, onOpenStockAdj = null,
    updateCell, cellValue, handleSave, saving, loadingMonth,
    closingFundCls, closingRiceCls,
    exportPdf, exportExcel, exportWord,
    allBalances, cascadedLedger,
  } = props;

  const classCard = (k) => {
    const cF = Number(consumed?.cost?.[k]) || 0;
    const cR = Number(consumed?.rice?.[k]) || 0;
    const closeF = closingFundCls[k] || 0;
    const closeR = closingRiceCls[k] || 0;
    // iter156 — Opening is editable ONLY on anchor month AND when the
    // user has flipped editingOpening on. Later months pin it read-only.
    const openEditable = isAnchorMonth && editingOpening;
    return (
      <div key={k} className="rounded-2xl border border-border bg-white p-4 shadow-[0_1px_0_rgba(44,53,40,0.04)]" data-testid={`m-fund-${k}-card`}>
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-heading text-base font-bold text-ink-900">{CLASS_LABELS[k]}</h3>
          <span className={`font-heading text-base font-bold ${closeF < 0 ? "text-destructive" : "text-clay"}`}>{money(closeF)}</span>
        </div>
        <p className="mt-0.5 text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-ink-700">Fund Ledger</p>
        <div className="mt-2 grid grid-cols-3 gap-2">
          <NumField label="Open" value={cellValue("openingFund", k)} readOnly={!openEditable} onChange={(v) => updateCell("openingFund", k, v)} testId={`m-fund-${k}-open-fund`} />
          <NumField label="Recv" value={cellValue("receivedFund", k)} onChange={(v) => updateCell("receivedFund", k, v)} testId={`m-fund-${k}-recv-fund`} />
          <ConsField total={cF} mdm={consumed?.mdm?.cost?.[k] ?? cF} dr={consumed?.dr?.cost?.[k] || 0} dec={2} testId={`m-fund-${k}-cons-fund`} />
        </div>

        <div className="mt-3 flex items-center justify-between gap-2 border-t border-border pt-3">
          <p className="text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-ink-700">Rice Ledger</p>
          <span className={`font-mono text-sm font-bold ${closeR < 0 ? "text-destructive" : "text-ink-900"}`}>{kg(closeR)}</span>
        </div>
        <div className="mt-2 grid grid-cols-3 gap-2">
          <NumField label="Open" value={cellValue("openingRice", k)} readOnly={!openEditable} onChange={(v) => updateCell("openingRice", k, v)} testId={`m-fund-${k}-open-rice`} step="0.001" />
          <NumField label="Recv" value={cellValue("receivedRice", k)} onChange={(v) => updateCell("receivedRice", k, v)} testId={`m-fund-${k}-recv-rice`} step="0.001" />
          <ConsField total={cR} mdm={consumed?.mdm?.rice?.[k] ?? cR} dr={consumed?.dr?.rice?.[k] || 0} dec={3} testId={`m-fund-${k}-cons-rice`} />
        </div>
      </div>
    );
  };

  return (
    <div className="md:hidden" data-testid="mobile-fund-management">
      {/* Hero */}
      <header className="px-5 pt-4">
        <p className="text-[0.7rem] font-semibold uppercase tracking-[0.24em] text-clay">Fund Management</p>
        <h1 className="mt-2 font-heading text-4xl font-bold text-ink-900">Class-wise Ledger</h1>
        <p className="mt-2 text-sm text-ink-700">
          <b className="text-ink-900">{active?.schoolName}</b>
          <span className="mx-1.5 text-ink-500">•</span>
          UDISE: {active?.udiseCode || "—"}
        </p>
      </header>

      {/* Month selector + lock state */}
      <section className="mx-4 mt-4 rounded-2xl border border-border bg-white p-4 shadow-[0_1px_0_rgba(44,53,40,0.04)]" data-testid="m-fund-controls">
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-ink-700">Year</span>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger data-testid="m-fund-year" className="mt-1 h-10 border-border bg-white text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{yearChoices.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </label>
          <label className="block">
            <span className="block text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-ink-700">Month</span>
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger data-testid="m-fund-month" className="mt-1 h-10 border-border bg-white text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{MONTH_OPTIONS.map((m) => <SelectItem key={m.v} value={String(m.v)}>{m.en}</SelectItem>)}</SelectContent>
            </Select>
          </label>
        </div>
        <label className="mt-3 block">
          <span className="block text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-ink-700">Reference Date</span>
          <input data-testid="m-fund-date" type="date" value={date} onChange={(e) => setDate(e.target.value)}
            className="mt-1 block w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-ink-900 outline-none focus:border-clay" />
        </label>
        {(onOpenFundAdj || onOpenStockAdj) && (
          <div className="mt-3 grid grid-cols-2 gap-2">
            {onOpenFundAdj && <button type="button" onClick={onOpenFundAdj} data-testid="m-fund-adj-btn" className="rounded-lg border border-border bg-white px-2 py-2 text-xs font-semibold text-ink-900">± Fund Adjustment ({(record?.fundAdjustments || []).length})</button>}
            {onOpenStockAdj && <button type="button" onClick={onOpenStockAdj} data-testid="m-stock-adj-btn" className="rounded-lg border border-border bg-white px-2 py-2 text-xs font-semibold text-ink-900">± Rice Stock Adj. ({(record?.stockAdjustments || []).length})</button>}
          </div>
        )}
        <div className="mt-3 flex items-center justify-between gap-3 rounded-xl bg-sand-50 px-3 py-2">
          {isAnchorMonth ? (
            <>
              <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${editingOpening ? "text-emerald-700" : "text-ink-700"}`}>
                {editingOpening ? <Unlock className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
                {editingOpening ? "Opening editable" : "Opening locked"}
              </span>
              {record.isLocked && (
                <button
                  type="button"
                  onClick={() => setEditingOpening((v) => !v)}
                  data-testid="m-fund-toggle-edit"
                  className="rounded-full border border-clay/30 bg-white px-3 py-1 text-[0.7rem] font-bold text-clay"
                >
                  {editingOpening ? "Done" : "Edit opening"}
                </button>
              )}
            </>
          ) : (
            <span
              className="inline-flex items-center gap-1.5 text-[0.65rem] font-semibold text-moss-dark"
              data-testid="m-fund-auto-opening-badge"
            >
              <Lock className="h-3.5 w-3.5" /> {isNewFundCascade() ? <>Opening = closing of {prevMonthLabel} (auto) · ପୂର୍ବ ମାସର ଅବଶିଷ୍ଟ</> : <>Opening = saved value for this month · ସଞ୍ଚିତ ପ୍ରାରମ୍ଭିକ ଅବଶିଷ୍ଟ</>}
            </span>
          )}
        </div>
      </section>

      {/* Class cards */}
      <section className="mx-4 mt-4 space-y-3">
        {CLASS_KEYS.map(classCard)}
        <AdjNote rec={record} group="receivedFund" unit="Rs" testId="m-fund-rs-adj-note" />
        <AdjNote rec={record} group="receivedRice" unit="kg" testId="m-fund-rice-adj-note" />
      </section>

      {/* Export actions */}
      <section className="mx-4 mt-4 rounded-2xl border border-border bg-white p-4 shadow-[0_1px_0_rgba(44,53,40,0.04)]" data-testid="m-fund-exports">
        <p className="text-[0.65rem] font-semibold uppercase tracking-[0.22em] text-ink-700">Download Ledger</p>
        <div className="mt-3 grid grid-cols-3 gap-2">
          <button onClick={exportPdf} data-testid="m-fund-pdf" className="flex flex-col items-center gap-1 rounded-xl border border-border bg-white py-3 text-xs font-semibold text-ink-900 active:bg-sand-50">
            <FileDown className="h-4 w-4 text-clay" /> PDF
          </button>
          <button onClick={exportExcel} data-testid="m-fund-excel" className="flex flex-col items-center gap-1 rounded-xl border border-border bg-white py-3 text-xs font-semibold text-ink-900 active:bg-sand-50">
            <FileSpreadsheet className="h-4 w-4 text-clay" /> Excel
          </button>
          <button onClick={exportWord} data-testid="m-fund-word" className="flex flex-col items-center gap-1 rounded-xl border border-border bg-white py-3 text-xs font-semibold text-ink-900 active:bg-sand-50">
            <FileText className="h-4 w-4 text-clay" /> Word
          </button>
        </div>
      </section>

      {/* History strip */}
      {(cascadedLedger?.size || 0) > 0 && (
        <section className="mx-4 mt-4 rounded-2xl border border-border bg-white p-4 shadow-[0_1px_0_rgba(44,53,40,0.04)]" data-testid="m-fund-history">
          <p className="flex items-center gap-2 text-[0.65rem] font-semibold uppercase tracking-[0.22em] text-ink-700">
            <Calendar className="h-3.5 w-3.5 text-clay" /> Monthly history
          </p>
          <ul className="mt-2 divide-y divide-border">
            {[...(cascadedLedger?.values() || [])].reverse().slice(0, 8).map((b) => {
              const led = cascadedLedger?.get(`${b.year}-${b.month}`);
              const totalCloseF = CLASS_KEYS.reduce((s, k) => {
                const oF = led ? led.opening.fund[k] : Number(b.openingFund?.[k] || 0);
                const rF = led ? led.received.fund[k] : Number(b.receivedFund?.[k] || 0);
                const cF = led ? led.consumed.fund[k] : 0;
                return s + (oF + rF - cF);
              }, 0);
              const monthName = MONTH_OPTIONS.find((m) => m.v === b.month)?.en || b.month;
              return (
                <li key={`${b.year}-${b.month}`} className="flex items-center justify-between gap-2 py-2.5" data-testid={`m-fund-history-${b.year}-${b.month}`}>
                  <div>
                    <p className="text-sm font-bold text-ink-900">
                      {monthName} {b.year}
                      {!b.saved && <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-[0.55rem] font-semibold uppercase tracking-wider text-slate-500" data-testid={`m-fund-history-auto-${b.year}-${b.month}`}>not saved · auto</span>}
                    </p>
                    <p className="text-[0.7rem] text-ink-700">
                      <Wallet className="mr-1 inline h-3 w-3 text-clay" />
                      Total Closing Fund
                    </p>
                  </div>
                  <span className={`font-heading text-sm font-bold ${totalCloseF < 0 ? "text-destructive" : "text-clay"}`}>{money(totalCloseF)}</span>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <div className="h-44" aria-hidden />

      {/* iter146 — Sticky Save via shared StickyActionBar. */}
      <StickyActionBar
        testId="m-fund-save"
        onClick={handleSave}
        disabled={saving || loadingMonth}
      >
        <Save className="h-4 w-4" /> {saving ? "Saving…" : loadingMonth ? "Loading…" : "Save & Lock Month"}
      </StickyActionBar>
    </div>
  );
}
